import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import {
  know,
  type ProfileDto,
  type ProfileOptions,
} from '../../api/know'
import { ApiError } from '../../api/client'
import {
  GENDER_LABELS,
  POSITION_LABELS,
  VR_EXPERIENCE_LABELS,
  footLabel,
  labelOf,
} from './profileLabels'
import { buildProfilePatch } from './profilePatch'
import { profileReturnTarget } from './profileReturn'
import type { KnowOutletCtx } from './KnowLayout'

/**
 * Edita el perfil del jugador en un solo lugar: identidad (email,
 * display_name), preferencia de juego (handedness, dominant_foot, altura)
 * y **datos demográficos** (edad, género, posición, experiencia VR,
 * notas visuales). Los demográficos antes eran un cuestionario aparte
 * (`DEMOGRAPHIC`); hoy viven acá.
 *
 * El endpoint devuelve `pending_profile_fields` — los campos requeridos
 * vacíos. Si la lista no está vacía el wizard arranca solo (modo
 * onboarding). No bloquea el juego (vrs-docs/15 §7.6): el texto no promete
 * "habilitar el VR".
 *
 * `?next=` (ver `profileReturn.ts`): al guardar vuelve ahí. Lo usa el aviso
 * de perfil incompleto del lobby del vr-app (`?next=/vr/`), que además
 * ofrece "Volver sin completar" porque el modo onboarding tapa la navegación.
 *
 * Durante el onboarding el wizard pide `setFullscreen(true)` a `KnowLayout`
 * para tapar header/tabs y ocupar toda la pantalla (experiencia dedicada,
 * no una página más del layout normal). El bloque grande de intro/contexto
 * ("Armemos tu perfil de jugador" + texto + progreso) se muestra sólo en el
 * primer paso; del segundo en adelante sólo queda un indicador de progreso
 * compacto arriba de la pregunta centrada. En modo "editing" (perfil ya
 * completo, el usuario entra a editar desde `ProfileSummary`) no es
 * onboarding, así que se mantiene el layout normal con el bloque de
 * contexto siempre visible.
 */
export function KnowProfile() {
  const { refresh, logout } = useAuth()
  const { setFullscreen } = useOutletContext<KnowOutletCtx>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = profileReturnTarget(searchParams.get('next'))
  const [profile, setProfile] = useState<ProfileDto | null>(null)
  const [options, setOptions] = useState<ProfileOptions | null>(null)
  const [step, setStep] = useState(0)
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const steps = useMemo(() => (options ? buildSteps(options) : []), [options])

  useEffect(() => {
    Promise.all([know.profile(), know.profileOptions()])
      .then(([p, o]) => {
        setProfile(p)
        setOptions(o)
      })
      .catch((e) => {
        const msg = e instanceof ApiError ? e.message : 'Error de red.'
        setError(msg)
      })
  }, [])

  const pending = new Set(profile?.pending_profile_fields ?? [])
  const isOnboarding = pending.size > 0

  useEffect(() => {
    setFullscreen(isOnboarding)
    return () => setFullscreen(false)
  }, [isOnboarding, setFullscreen])

  if (error && !profile) return <div className="banner-err">{error}</div>
  if (!profile || !options)
    return <p className="text-sm text-text-muted">Cargando…</p>

  const showWizard = isOnboarding || editing
  const current = steps[Math.min(step, steps.length - 1)]
  const progressPct = Math.round(
    ((Math.min(step, steps.length - 1) + 1) / steps.length) * 100,
  )
  const showIntro = !isOnboarding || step === 0

  async function saveProfile() {
    if (!profile) return
    setBusy(true)
    setError(null)
    try {
      const updated = await know.updateProfile(buildProfilePatch(profile))
      setProfile(updated)
      setSavedAt(Date.now())
      setEditing(false)
      setStep(0)
      // Refresh /auth/me para que `pending_profile_fields` se actualice
      // a nivel global (libera el botón Train en HomePage si quedó vacío).
      await refresh()
      if (returnTo) leave()
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Error de red.'
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  function leave() {
    if (!returnTo) return
    if (returnTo.reload) window.location.assign(returnTo.path)
    else navigate(returnTo.path)
  }

  function setField<K extends keyof ProfileDto>(key: K, value: ProfileDto[K]) {
    setProfile((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  function hasCurrentValue(): boolean {
    if (!profile) return false
    if (!current) return true
    if (!current.required) return true
    const value = profile[current.key]
    if (typeof value === 'string') return value.trim().length > 0
    return value !== null && value !== undefined
  }

  function next() {
    if (!hasCurrentValue()) {
      setError('Respondé esta pregunta para continuar.')
      return
    }
    setError(null)
    if (step >= steps.length - 1) {
      void saveProfile()
      return
    }
    setStep((s) => s + 1)
  }

  return (
    <section className={isOnboarding ? 'flex min-h-screen items-center justify-center p-lg' : undefined}>
      {showWizard ? (
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-lg">
          {showIntro && (
            <div className="rounded-md border border-primary/40 bg-surface-elevated p-lg shadow-[0_0_34px_rgba(226,90,27,0.10)]">
              <span className={isOnboarding ? 'tag-ok' : 'tag'} aria-live="polite">
                {isOnboarding ? 'Perfil incompleto' : 'Editando perfil'}
              </span>
              <h1 className="mt-sm">Armemos tu perfil de jugador</h1>
              <p className="m-0 text-sm leading-relaxed text-text-secondary">
                Son preguntas cortas y sirven para interpretar mejor tus
                resultados.
              </p>
              <div className="mt-md flex items-center gap-md text-xs text-text-secondary">
                <span>
                  {step + 1} / {steps.length}
                </span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-highlight">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <span>{progressPct}%</span>
              </div>
            </div>
          )}

          {!showIntro && (
            <div className="flex items-center gap-md text-xs text-text-secondary">
              <span>
                {step + 1} / {steps.length}
              </span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-highlight">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span>{progressPct}%</span>
            </div>
          )}

          {error && <div className="banner-err">{error}</div>}

          {current && (
            <div className="card flex flex-col gap-lg border-primary/30">
              <div>
                <div className="text-xs uppercase tracking-wide text-text-muted">
                  {current.required ? 'Requerida' : 'Opcional'}
                </div>
                <h2 className="m-0 mt-xs normal-case tracking-normal text-lg font-bold text-text-primary">
                  {current.title}
                </h2>
                {current.help && (
                  <p className="m-0 mt-xs text-sm text-text-secondary">{current.help}</p>
                )}
              </div>

              <ProfileQuestion
                field={current}
                profile={profile}
                options={options}
                setField={setField}
              />

              <div className="flex items-center justify-between gap-sm">
                <button
                  type="button"
                  className="btn flex-1 sm:flex-none"
                  disabled={step === 0 || busy}
                  onClick={() => {
                    setError(null)
                    setStep((s) => Math.max(0, s - 1))
                  }}
                >
                  ← Anterior
                </button>
                {!current.required && step < steps.length - 1 && (
                  <button
                    type="button"
                    className="btn-ghost shrink-0 px-md text-xs"
                    disabled={busy}
                    onClick={() => {
                      setError(null)
                      setStep((s) => s + 1)
                    }}
                  >
                    Saltear
                  </button>
                )}
                <button
                  type="button"
                  className="btn-primary flex-1 sm:flex-none"
                  disabled={busy}
                  onClick={next}
                >
                  {busy
                    ? 'Guardando…'
                    : step >= steps.length - 1
                      ? 'Guardar perfil'
                      : 'Siguiente →'}
                </button>
              </div>
            </div>
          )}

          {returnTo && (
            <button
              type="button"
              className="btn-ghost self-center text-xs"
              disabled={busy}
              onClick={leave}
            >
              Volver sin completar
            </button>
          )}
        </div>
      ) : (
        <ProfileSummary
          profile={profile}
          savedAt={savedAt}
          onEdit={() => setEditing(true)}
          onLogout={logout}
        />
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------

type ProfileFieldKey =
  | 'display_name'
  | 'age'
  | 'gender'
  | 'position'
  | 'vr_experience'
  | 'dominant_foot'
  | 'height_cm'
  | 'vision_notes'

interface ProfileStep {
  key: ProfileFieldKey
  title: string
  help?: string
  required: boolean
}

function buildSteps(options: ProfileOptions): ProfileStep[] {
  const required = new Set(options.required_for_vr)
  return [
    {
      key: 'display_name',
      title: '¿Cómo querés que te llamemos?',
      required: true,
    },
    {
      key: 'age',
      title: '¿Qué edad tenés?',
      required: required.has('age'),
    },
    {
      key: 'gender',
      title: '¿Con qué género te identificás?',
      required: required.has('gender'),
    },
    {
      key: 'position',
      title: '¿Cuál es tu posición principal?',
      required: required.has('position'),
    },
    {
      key: 'vr_experience',
      title: '¿Ya usaste realidad virtual?',
      required: required.has('vr_experience'),
    },
    {
      key: 'dominant_foot',
      title: '¿Cuál es tu pie dominante?',
      required: false,
    },
    {
      key: 'height_cm',
      title: '¿Cuánto medís?',
      required: false,
    },
    {
      key: 'vision_notes',
      title: '¿Hay algo visual que debamos saber?',
      help: 'Ejemplo: miopía, daltonismo, lesión reciente. Podés saltearlo.',
      required: false,
    },
  ]
}

function ProfileQuestion({
  field,
  profile,
  options,
  setField,
}: {
  field: ProfileStep
  profile: ProfileDto
  options: ProfileOptions
  setField: <K extends keyof ProfileDto>(key: K, value: ProfileDto[K]) => void
}) {
  if (field.key === 'display_name') {
    return (
      <input
        type="text"
        className="input"
        value={profile.display_name ?? ''}
        maxLength={120}
        autoFocus
        onChange={(e) => setField('display_name', e.target.value)}
      />
    )
  }
  if (field.key === 'age') {
    return (
      <div className="w-full max-w-sm rounded-md border border-primary/30 bg-surface-highlight/40 p-md">
        <input
          type="number"
          className="input max-w-[140px] text-lg font-bold tabular-nums"
          value={profile.age ?? ''}
          min={options.age.min}
          max={options.age.max}
          inputMode="numeric"
          autoFocus
          onChange={(e) =>
            setField('age', e.target.value === '' ? null : Number(e.target.value))
          }
        />
        <p className="mt-sm text-xs text-text-muted">
          mín. {options.age.min} · máx. {options.age.max}
        </p>
      </div>
    )
  }
  if (field.key === 'height_cm') {
    return (
      <div className="flex items-baseline gap-md">
        <input
          type="number"
          className="input max-w-[140px] text-lg font-bold tabular-nums"
          value={profile.height_cm ?? ''}
          min={80}
          max={250}
          inputMode="numeric"
          onChange={(e) =>
            setField('height_cm', e.target.value === '' ? null : Number(e.target.value))
          }
        />
        <span className="text-sm text-text-muted">cm</span>
      </div>
    )
  }
  if (field.key === 'vision_notes') {
    return (
      <textarea
        className="input min-h-[120px] resize-y"
        value={profile.vision_notes ?? ''}
        maxLength={options.vision_notes_max_length}
        onChange={(e) =>
          setField('vision_notes', e.target.value === '' ? null : e.target.value)
        }
        placeholder="Dejalo vacío si no aplica."
      />
    )
  }

  const choices =
    field.key === 'gender'
      ? options.gender.map((v) => ({ value: v, label: GENDER_LABELS[v] ?? v }))
      : field.key === 'position'
        ? options.position.map((v) => ({ value: v, label: POSITION_LABELS[v] ?? v }))
        : field.key === 'vr_experience'
          ? options.vr_experience.map((v) => ({
              value: v,
              label: VR_EXPERIENCE_LABELS[v] ?? v,
            }))
          : [
              { value: 'right', label: 'Derecho' },
              { value: 'left', label: 'Izquierdo' },
              { value: 'both', label: 'Ambos' },
            ]

  return (
    <div className="grid grid-cols-1 gap-sm sm:grid-cols-2">
      {choices.map((choice) => {
        const selected = profile[field.key] === choice.value
        return (
          <button
            key={choice.value}
            type="button"
            onClick={() =>
              setField(
                field.key,
                choice.value as ProfileDto[typeof field.key],
              )
            }
            className={[
              'choice-card',
              selected ? 'choice-card-selected' : '',
            ].join(' ')}
          >
            <span
              className={[
                'h-4 w-4 shrink-0 rounded-full border',
                selected ? 'border-primary bg-primary' : 'border-outline',
              ].join(' ')}
            />
            <span>{choice.label}</span>
          </button>
        )
      })}
    </div>
  )
}

function ProfileSummary({
  profile,
  savedAt,
  onEdit,
  onLogout,
}: {
  profile: ProfileDto
  savedAt: number | null
  onEdit: () => void
  onLogout: () => Promise<void>
}) {
  return (
    <div>
      <div className="rounded-md border border-primary/40 bg-surface-elevated p-lg shadow-[0_0_34px_rgba(226,90,27,0.10)]">
        <span className="tag-ok">Perfil listo</span>
        <h1 className="mt-sm">Perfil</h1>
        <p className="m-0 max-w-2xl text-sm text-text-secondary">
          Estos datos ayudan a personalizar el entrenamiento y contextualizar
          tus resultados.
        </p>
      </div>
      {savedAt && <div className="banner-ok mt-md">Perfil actualizado.</div>}
      <div className="mt-lg grid grid-cols-1 gap-md sm:grid-cols-2">
        <SummaryItem label="Nombre" value={profile.display_name || profile.username} />
        <SummaryItem label="Email" value={profile.email || '—'} />
        <SummaryItem label="Edad" value={profile.age ? `${profile.age} años` : '—'} />
        <SummaryItem label="Género" value={labelOf(profile.gender, GENDER_LABELS)} />
        <SummaryItem label="Posición" value={labelOf(profile.position, POSITION_LABELS)} />
        <SummaryItem
          label="Experiencia VR"
          value={labelOf(profile.vr_experience, VR_EXPERIENCE_LABELS)}
        />
        <SummaryItem
          label="Pie dominante"
          value={footLabel(profile.dominant_foot)}
        />
        <SummaryItem
          label="Altura"
          value={profile.height_cm ? `${profile.height_cm} cm` : '—'}
        />
      </div>
      <div className="mt-lg flex flex-wrap gap-sm">
        <button type="button" className="btn-primary" onClick={onEdit}>
          Editar perfil
        </button>
        <button type="button" className="btn" onClick={() => void onLogout()}>
          Cerrar sesión
        </button>
      </div>
    </div>
  )
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="card border-primary/30">
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className="mt-xs text-base font-medium text-text-primary">{value}</div>
    </div>
  )
}
